// ─────────────────────────────────────────────────────────────────────────────
// Layout.tsx — Page shell wrapping Navbar + main content + Footer
//
// Also renders the "active pattern" banner below the navbar when a non-default
// pattern is selected, so it's always clear which architecture is being
// demonstrated.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { MobileTabBar } from './MobileTabBar';
import { BackToTopButton } from './BackToTopButton';
import { MobileSheet } from './MobileSheet';
import { PagesSheetContent } from './mobileSheetContent/PagesSheetContent';
import { CustomizeSheetContent } from './mobileSheetContent/CustomizeSheetContent';
import { MoreSheetContent } from './mobileSheetContent/MoreSheetContent';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useTheme } from '@/hooks/useTheme';
import { applyColourScheme } from '@/styles/colourSchemes';
import { closeMobileSheet } from '@/store/uiSlice';

const MOBILE_SHEET_TITLES: Record<string, string> = {
  pages:     'Pages',
  customize: 'Customize',
  more:      'More',
};

// Pattern mode labels and colours for the banner
const PATTERN_BANNERS: Record<string, { label: string; colour: string }> = {
  mvc:    { label: 'MVC Mode — Model → View → Controller',               colour: 'bg-blue-600' },
  mvvm:   { label: 'MVVM Mode — ViewModel hooks, purely declarative View', colour: 'bg-purple-600' },
  atomic: { label: 'Atomic Design — Atoms → Molecules → Organisms',       colour: 'bg-emerald-600' },
};

export function Layout() {
  // useTheme initialises the <html> class — must be called inside a Provider
  const { resolved } = useTheme();
  const dispatch = useAppDispatch();

  const activePattern = useAppSelector((s) => s.ui.frontEndPattern);
  const colourScheme  = useAppSelector((s) => s.ui.colourScheme);
  const cvLayout      = useAppSelector((s) => s.ui.cvLayout);
  const mobileSheet   = useAppSelector((s) => s.ui.mobileSheet);
  const banner        = PATTERN_BANNERS[activePattern];

  // Apply colour scheme CSS variables whenever scheme or dark/light mode changes
  useEffect(() => {
    applyColourScheme(colourScheme, resolved === 'dark');
  }, [colourScheme, resolved]);

  // Apply layout data-attribute so CSS selectors like html[data-layout="minimal"] work
  useEffect(() => {
    document.documentElement.setAttribute('data-layout', cvLayout);
  }, [cvLayout]);

  return (
    // 'min-h-screen flex flex-col' ensures the footer always sits at the bottom
    <div className="min-h-screen flex flex-col bg-[var(--color-bg)] text-[var(--color-text)] transition-colors duration-300">

      {/* Fixed top nav */}
      <Navbar />

      {/* Pattern indicator banner (only shown for non-default patterns) */}
      <AnimatePresence>
        {banner && (
          <motion.div
            key={activePattern}
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0,   opacity: 1 }}
            exit={{ y: -30, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={`${banner.colour} text-white text-sm font-medium py-2 text-center fixed top-0 md:top-22 left-0 right-0 z-40`}
          >
            {banner.label}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content area. On mobile there's no fixed top header any more
          (removed entirely — nav/customization live in MobileTabBar), so
          no top padding is needed there; desktop still has the navbar.
          pb-20 on mobile keeps content clear of the fixed MobileTabBar. */}
      <main className={`flex-1 pt-0 md:pt-22 pb-20 md:pb-0 ${banner ? 'mt-9' : ''}`}>
        {/* React Router renders the matched page component here */}
        <Outlet />
      </main>

      <Footer />

      {/* Mobile-only bottom navigation + its sheets — md:hidden inside each component */}
      <BackToTopButton />
      <MobileTabBar />
      <MobileSheet
        isOpen={mobileSheet !== null}
        title={mobileSheet ? MOBILE_SHEET_TITLES[mobileSheet] : ''}
        onClose={() => dispatch(closeMobileSheet())}
      >
        {mobileSheet === 'pages' && <PagesSheetContent />}
        {mobileSheet === 'customize' && <CustomizeSheetContent />}
        {mobileSheet === 'more' && <MoreSheetContent />}
      </MobileSheet>
    </div>
  );
}
